import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import type { FindingAttribute } from '@/models/template';
import type { TreeNode } from '../types/findingHierarchy';
import { getFragmentAroundCaret } from '../utils/textHelpers';
import { navigateWorksheetTab } from '../../../shared/utils/tabNavigation';
import {
  buildTreeFromAttributes,
  buildTreeWithClassFolders,
} from '../utils/treeBuilder';
import { filterAndScoreOptions } from '../utils/filterOptions';
import {
  toggleStatementInRow,
  withoutSummary,
  applyAllNormal,
  isAllNormalActive,
  orderByRow,
} from '../utils/selectionHelpers';

/** Sentinel grid cell marking the "All normal" button's navigable position
 *  (its own single-cell nav row, above the class's attribute rows). */
const ALL_NORMAL = 'all-normal' as const;
type GridCell = TreeNode | typeof ALL_NORMAL;

interface UseHierarchyCompletionArgs {
  value: string;
  onChange: (value: string) => void;
  attributes: FindingAttribute[];
  regionLabel?: string;
  findingClassLabels?: { findingClass: string; label: string }[];
  /** Statements already committed upstream — drives pill highlight + toggle. */
  selectedStatements?: string[];
  /** Batch replace of the committed statement list (single-select swap,
   *  "All normal"). */
  onSetStatements?: (next: string[]) => void;
}

export const useHierarchyCompletion = ({
  value,
  onChange,
  attributes,
  regionLabel,
  findingClassLabels,
  selectedStatements,
  onSetStatements,
}: UseHierarchyCompletionArgs) => {
  const textAreaRef = useRef<TextAreaRef | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const suppressBlurRef = useRef(false);
  const isKeyboardNavRef = useRef(false);
  // Set by `FindingGridPicker` to the currently-focused pill DOM node so
  // arrow-key navigation can consult its wrapped-row siblings when pills
  // overflow to multiple visual lines (wrap-aware Down/Up).
  const focusedPillDomRef = useRef<HTMLButtonElement | null>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [gridRow, setGridRow] = useState(0);
  const [gridCol, setGridCol] = useState(0);
  const [pathStack, setPathStack] = useState<TreeNode[]>([]);

  // ---- Build tree once from attributes ----

  const rootNodes = useMemo(() => {
    // When multiple finding classes are grouped, add a folder level per class
    if (findingClassLabels && findingClassLabels.length > 1) {
      const attributesByClass = new Map<string, FindingAttribute[]>();
      findingClassLabels.forEach((cl) => {
        attributesByClass.set(
          cl.findingClass,
          attributes.filter((a) => a.findingClass === cl.findingClass)
        );
      });
      return buildTreeWithClassFolders(attributesByClass, findingClassLabels);
    }
    return buildTreeFromAttributes(attributes, regionLabel);
  }, [attributes, regionLabel, findingClassLabels]);

  // ---- Current level nodes ----

  const currentLevelNodes = useMemo<TreeNode[]>(() => {
    if (pathStack.length === 0) return rootNodes;
    return pathStack[pathStack.length - 1].children;
  }, [rootNodes, pathStack]);

  // ---- Fuzzy filter within current level ----

  const visibleOptions = useMemo(() => {
    if (!open) return [];
    return filterAndScoreOptions(currentLevelNodes, query);
  }, [currentLevelNodes, open, query]);

  // ---- Build the keyboard-nav grid ----
  // Mirrors the rows FindingGridPicker renders so gridRow/gridCol map to exactly
  // what the user sees. Per class: an "All normal" row (single ALL_NORMAL cell)
  // followed by one row per attribute (its statement leaves). `rowMeta` is aligned
  // 1:1 with grid rows, holding each row's owning-class id (undefined for the flat
  // single-class shape) so Enter/all-normal can resolve the full class.
  const { grid, rowMeta } = useMemo(() => {
    const g: GridCell[][] = [];
    const meta: (string | undefined)[] = [];
    if (visibleOptions.length === 0) return { grid: g, rowMeta: meta };
    const isClassFolderShape = visibleOptions.some(
      (n) => n.children.length > 0 && !n.children[0].isLeaf
    );
    const addClass = (classId: string | undefined, nodes: TreeNode[]) => {
      const classRows = nodes.filter((r) => r.children.length > 0);
      if (classRows.length === 0) return;
      g.push([ALL_NORMAL]);
      meta.push(classId);
      classRows.forEach((layer2) => {
        g.push(layer2.children);
        meta.push(classId);
      });
    };
    if (isClassFolderShape) {
      visibleOptions.forEach((cf) => addClass(cf.id, cf.children));
    } else {
      addClass(undefined, visibleOptions);
    }
    return { grid: g, rowMeta: meta };
  }, [visibleOptions]);

  // Clamp gridPos when the grid shape changes (filtered visibleOptions,
  // new pills appear, etc.) so the focus indicator never points off-grid.
  useEffect(() => {
    if (grid.length === 0) {
      setGridRow(0);
      setGridCol(0);
      return;
    }
    if (gridRow >= grid.length) setGridRow(grid.length - 1);
    const row = grid[Math.min(gridRow, grid.length - 1)];
    if (row && gridCol >= row.length) setGridCol(Math.max(row.length - 1, 0));
  }, [grid, gridRow, gridCol]);

  // ---- Close dropdown when typing yields no results ----

  useEffect(() => {
    if (open && visibleOptions.length === 0 && query !== '') {
      setOpen(false);
    }
  }, [open, visibleOptions.length, query]);

  // ---- Reset when value is externally cleared ----
  //
  // "Empty ⇒ open" but ONLY when the input is currently focused — otherwise
  // every add-input mounts with value='' and every dropdown would render open
  // on page load. Focus is the signal that the reader is interacting with
  // this row; without it, we clear the search state but leave `open` alone.
  useEffect(() => {
    if (!value) {
      setPathStack([]);
      setQuery('');
      const el = textAreaRef.current?.resizableTextArea?.textArea;
      if (el && document.activeElement === el) setOpen(true);
    }
  }, [value]);

  // ---- Close on outside click ----

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // ---- Manual height sync (replaces autoSize) ----

  useEffect(() => {
    const el = textAreaRef.current?.resizableTextArea?.textArea;
    if (!el) return;
    el.style.height = 'auto';
    const { scrollHeight } = el;
    if (Number.isFinite(scrollHeight) && scrollHeight > 0) {
      el.style.height = `${scrollHeight}px`;
    }
  }, [value]);

  // ---- Query from caret fragment ----

  const refreshQueryFromText = (nextText: string, nextCaret: number) => {
    const { fragment } = getFragmentAroundCaret(nextText, nextCaret);
    setQuery(fragment);
  };

  // ---- Handlers ----

  // Typing closes the dropdown so the reader can free-type a custom statement
  // without the picker on top of them; deleting back to empty reopens it so
  // Tab-nav'ed users don't have to click to bring the pills back.
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const nextVal = e.target.value;
    const caret = e.target.selectionStart ?? nextVal.length;
    onChange(nextVal);
    refreshQueryFromText(nextVal, caret);
    setOpen(nextVal.trim() === '');
  };

  // Resolve a class's FULL (unfiltered) attribute rows + all-normal summary from
  // rootNodes. Selection/all-normal must operate on the whole class even when a
  // search query has filtered the visible rows/pills — otherwise non-visible
  // selections wouldn't be cleared and non-visible rows would be skipped.
  // classFolderId is undefined for the flat single-class shape (whole input).
  const resolveClass = useCallback(
    (classFolderId?: string) => {
      const source = classFolderId
        ? (rootNodes.find((n) => n.id === classFolderId)?.children ?? [])
        : rootNodes;
      const rows = source.filter((r) => r.children.length > 0);
      const summary = rows[0]?.allNormalText;
      return { rows, summary };
    },
    [rootNodes]
  );

  // Every attribute row across the whole input, regardless of whether the
  // tree is class-folder-shaped (multi-class group like "Atria" pooling
  // LEFT_ATRIUM + RIGHT_ATRIUM) or flat (single-class input). Used to feed
  // orderByRow so picks from *any* class end up in worksheet display order
  // — otherwise selections from a non-active class get demoted to the tail
  // on every subsequent pick.
  const allAttributeRows = useMemo<TreeNode[]>(() => {
    const isClassFolderShape = rootNodes.some(
      (n) => n.children.length > 0 && !n.children[0].isLeaf
    );
    const source = isClassFolderShape
      ? rootNodes.flatMap((folder) => folder.children)
      : rootNodes;
    return source.filter((r) => r.children.length > 0);
  }, [rootNodes]);

  // Pill selection (mouse + keyboard). Single-select per row: selecting a pill
  // swaps out any other selected statement in the same attribute row. Picking
  // anything also clears the class's all-normal summary (they're mutually
  // exclusive). Downstream conclusion-bullet sync runs off the committed
  // findings state, not off this callback — see useReportEditorState.
  const selectLeaf = useCallback(
    (leaf: TreeNode, classFolderId?: string) => {
      const { rows: classRows, summary } = resolveClass(classFolderId);
      const rowLeaves = classRows.find((r) =>
        r.children.some((l) => l.id === leaf.id)
      )?.children ?? [leaf];
      const current = selectedStatements ?? [];
      const next = toggleStatementInRow(
        withoutSummary(current, summary),
        leaf,
        rowLeaves
      );
      onSetStatements?.(orderByRow(next, allAttributeRows));
      // Reset the search box + query so the full list returns; keep the
      // dropdown open for continued selection.
      onChange('');
      setQuery('');
      setOpen(true);
      setPathStack([]);
      textAreaRef.current?.focus();
    },
    [
      selectedStatements,
      onSetStatements,
      onChange,
      resolveClass,
      allAttributeRows,
    ]
  );

  // Collapse a finding class to (or out of) its single "all normal" summary.
  const toggleAllNormal = useCallback(
    (classFolderId?: string) => {
      const { rows: classRows, summary } = resolveClass(classFolderId);
      const current = selectedStatements ?? [];
      const turnOn = !isAllNormalActive(current, summary);
      onSetStatements?.(applyAllNormal(current, classRows, summary, turnOn));
      onChange('');
      setQuery('');
      setOpen(true);
      textAreaRef.current?.focus();
    },
    [selectedStatements, onSetStatements, onChange, resolveClass]
  );

  // Wrap-aware Down/Up: when a row's pills overflow to multiple visual lines,
  // Down should first walk to the pill directly below within the SAME row
  // before crossing into the next attribute. Returns the target column index
  // within the current row, or null when no wrapped neighbor is directly
  // below/above (caller then jumps to first pill of next/prev attribute row).
  //
  // Direct-below = the sibling pill whose top offset is strictly greater than
  // the current pill's AND whose horizontal span overlaps the current pill's
  // center. Uses layout offsets (not tab index) so it stays correct when the
  // flex container reflows on window resize.
  const pillWrapNeighbor = (direction: 'up' | 'down'): number | null => {
    const { current } = focusedPillDomRef;
    if (!current) return null;
    const parent = current.parentElement;
    if (!parent) return null;
    const pills = Array.from(parent.children).filter(
      (n): n is HTMLElement => n instanceof HTMLElement
    );
    const curIdx = pills.indexOf(current);
    if (curIdx === -1) return null;
    const cur = current.getBoundingClientRect();
    const curCenter = cur.left + cur.width / 2;
    const wantLower = direction === 'down';
    // Find candidates on a strictly lower/higher visual line.
    const candidates = pills
      .map((p, i) => ({ p, i, rect: p.getBoundingClientRect() }))
      .filter(({ rect }) =>
        wantLower ? rect.top > cur.bottom - 1 : rect.bottom < cur.top + 1
      );
    if (candidates.length === 0) return null;
    // Nearest visual line = min top gap for down, min top gap for up.
    candidates.sort((a, b) =>
      wantLower ? a.rect.top - b.rect.top : b.rect.bottom - a.rect.bottom
    );
    const targetTop = candidates[0].rect.top;
    const sameLine = candidates.filter(
      (c) => Math.abs(c.rect.top - targetTop) < 1
    );
    // Pick the pill on that line whose horizontal span covers the current
    // pill's center. Fall back to the closest by horizontal-center distance.
    const covering = sameLine.find(
      ({ rect }) => rect.left <= curCenter && rect.right >= curCenter
    );
    const chosen =
      covering ??
      sameLine.sort(
        (a, b) =>
          Math.abs(a.rect.left + a.rect.width / 2 - curCenter) -
          Math.abs(b.rect.left + b.rect.width / 2 - curCenter)
      )[0];
    return chosen.i;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const current = e.currentTarget;
    const currentCaret = current.selectionStart ?? value.length;

    if ((e.ctrlKey || e.metaKey) && e.key === ' ') {
      e.preventDefault();
      refreshQueryFromText(value, currentCaret);
      return;
    }

    // Tab: navigate across the whole worksheet (Enter selects from autocomplete).
    // Close this input's dropdown first; the next input's own focus handler
    // opens its dropdown when appropriate (empty add-input → open).
    if (e.key === 'Tab') {
      e.preventDefault();
      setOpen(false);
      const el = textAreaRef.current?.resizableTextArea?.textArea;
      if (el) navigateWorksheetTab(el, e.shiftKey);
      return;
    }

    if (e.key === 'Escape') {
      setOpen(false);
      textAreaRef.current?.blur();
      return;
    }

    if (!open || grid.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      isKeyboardNavRef.current = true;
      const wrapped = pillWrapNeighbor('down');
      if (wrapped !== null) {
        setGridCol(wrapped);
        return;
      }
      // Move to first pill of next attribute row. When already on the last
      // row we no-op instead of snapping col back to 0 — otherwise Down at
      // the bottom would jump from any pill to the first pill of the same
      // row (visual wrap that has no destination below).
      if (gridRow >= grid.length - 1) return;
      setGridRow(gridRow + 1);
      setGridCol(0);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      isKeyboardNavRef.current = true;
      const wrapped = pillWrapNeighbor('up');
      if (wrapped !== null) {
        setGridCol(wrapped);
        return;
      }
      // Same guard on the top row.
      if (gridRow <= 0) return;
      setGridRow(gridRow - 1);
      setGridCol(0);
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      isKeyboardNavRef.current = true;
      setGridCol((c) => Math.max(c - 1, 0));
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      isKeyboardNavRef.current = true;
      const row = grid[gridRow];
      if (row) setGridCol((c) => Math.min(c + 1, row.length - 1));
      return;
    }

    if (e.key === 'Enter') {
      const cell = grid[gridRow]?.[gridCol];
      if (!cell) return;
      e.preventDefault();
      const classFolderId = rowMeta[gridRow];
      if (cell === ALL_NORMAL) toggleAllNormal(classFolderId);
      else selectLeaf(cell, classFolderId);
    }
  };

  // On blur we close after a short debounce so a mousedown inside the
  // dropdown (which flips suppressBlurRef via onMouseDown on the panel)
  // can cancel the close before it fires — otherwise clicking a pill
  // would first close the panel and then never dispatch the click.
  const handleBlur = () => {
    setTimeout(() => {
      if (suppressBlurRef.current) {
        suppressBlurRef.current = false;
        return;
      }
      setOpen(false);
    }, 120);
  };

  // Open the dropdown any time the empty add-input takes focus (mouse click,
  // Tab, programmatic). Typing narrows/closes it via handleChange; clearing
  // back to empty reopens via the same rule. The rule "empty ⇒ open" is
  // enforced everywhere `value` is set.
  const onTextAreaFocus = () => {
    if (value.trim() === '') setOpen(true);
  };

  // Kept as a no-op for parity with earlier onMouseDown wiring; the focus
  // handler now opens the dropdown for both click and keyboard entry.
  const handleMouseDown = useCallback(() => {}, []);

  const handleClear = () => {
    onChange('');
    setQuery('');
    // Clearing the free-text back to empty reopens the picker; the reader
    // was likely typing to search and now wants the full list again.
    setOpen(true);
    setPathStack([]);
  };

  const suppressBlur = useCallback(() => {
    suppressBlurRef.current = true;
  }, []);

  const clearKeyboardNav = useCallback(() => {
    isKeyboardNavRef.current = false;
  }, []);

  const setGridPos = useCallback((row: number, col: number) => {
    setGridRow(row);
    setGridCol(col);
  }, []);

  return {
    textAreaRef,
    wrapperRef,
    isKeyboardNavRef,
    focusedPillDomRef,
    suppressBlur,
    clearKeyboardNav,
    open,
    query,
    visibleOptions,
    gridRow,
    gridCol,
    setGridPos,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleMouseDown,
    onTextAreaFocus,
    handleClear,
    selectLeaf,
    toggleAllNormal,
  };
};
